import { useState } from 'react';
import { useLocation } from 'react-router';
import {
    getPendingHeroImage,
    PROJECT_HERO_TRANSITION_NAME,
    useTileHeroViewTransitionStyle,
} from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';
import { useProjectPreviewRouteData } from '@modules/project/hook/use-project-preview-route-data';
import {
    DetailBadgeRow,
    DetailBreadcrumbBar,
    DetailBylineRow,
    DetailHero,
    DetailImageOverlay,
    MOBILE_DETAIL_BYLINE,
    MOBILE_DETAIL_TITLE,
    detailStatsFromProfileKpi,
    type OverlaySlide,
} from '~/ui-rocco/pages/detail-shared';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { SectionSpacer } from '~/ui-rocco/pages/home-page/components/SectionSpacer';
import { PagesEnum } from '~/utils/route-config';
import { collectProjectImages } from './collect-project-images';
import { BoostResolutionOverlay } from '@modules/page-builder/component/common/upscale-warning/BoostResolutionOverlay';
import { useMatchesHandleData } from '~/modules/root/hook';
import { useCanDownloadImages } from '@modules/user';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { HeroImageActions } from '~/ui-rocco/pages/detail-shared/HeroImageActions';
import { getRevampHeroUpscaleTarget } from '~/ui-rocco/pages/detail-shared/hero-upscale-target';

/**
 * P2 — project hero. Full-bleed banner photo with the project name overlaid.
 * Maps the project loader into the shared {@link DetailHero} image variant,
 * overriding its geometry to the taller project frame (1920×1373) with the
 * title set mid-lower rather than pinned to the bottom edge. Reads the
 * preview-merged project so BM template-preview drafts show the edited banner.
 *
 * The banner box is the morph counterpart of a listing project tile
 * (`project-hero`); the underlay covers the cold-cache gap with the tile's
 * already-decoded rendition.
 */
export const ProjectHeroSection = () => {
    const project = useProjectPreviewRouteData();
    const isBMPreview = useMatchesHandleData('isBMPreview', false);
    const canDownloadImages = useCanDownloadImages();
    const { desktop } = useAppDisplayModeContext();
    const { pathname } = useLocation();
    const heroStyle = useTileHeroViewTransitionStyle(
        pathname,
        PROJECT_HERO_TRANSITION_NAME,
    );
    const [underlaySrc] = useState(() => getPendingHeroImage(pathname));
    // The banner in the project's photo viewer, leading the gallery's
    // photographs, as the About image is.
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerIndex, setViewerIndex] = useState(0);

    // Fall back to the social preview image when the banner has no still — e.g.
    // video-only banners — so the hero never disappears.
    const backgroundImage =
        project.Banner?.imageUrl || project.FacebookPreviewImage;
    if (!backgroundImage) return null;

    // A banner set to video in the BM plays over the still. Matching the legacy
    // banner, a stored video URL is enough on its own — some banners carry the
    // URL without the flag having been re-saved.
    const videoBannerUrl = project.Banner?.videoBannerUrl;
    const backgroundVideo =
        project.Banner?.isVideoBanner || videoBannerUrl
            ? videoBannerUrl
            : undefined;

    const professional = project.Professional;
    const category = project.Categories?.[0];
    const gallery = collectProjectImages(project.Modules, project.Title);
    const slides: OverlaySlide[] = gallery.some(
        (slide) => slide.src === backgroundImage,
    )
        ? gallery
        : [
              {
                  src: backgroundImage,
                  alt: project.Title,
                  // The banner is a library image like any other, so a save
                  // from the viewer pins the photograph rather than the
                  // project standing in for it.
                  id: project.Banner?.libraryImage?.ID ?? undefined,
              },
              ...gallery,
          ];
    const openViewer = () => {
        setViewerIndex(
            Math.max(
                0,
                slides.findIndex((slide) => slide.src === backgroundImage),
            ),
        );
        setViewerOpen(true);
    };

    // Staff can boost the banner the page opens on, as they can the
    // photographs under it. Not a video banner: the still is not what shows.
    const bannerImageId = project.Banner?.libraryImage?.ID ?? 0;
    const canBoostBanner = !backgroundVideo && bannerImageId > 0;
    const upscaleTarget = getRevampHeroUpscaleTarget();
    // The banner on its own, as a file, for the profile's team and staff — the
    // legacy banner offered it and the photographs below still do. Desktop
    // only, as there, and not in the Business Manager preview.
    const readerDownloadLink =
        canDownloadImages && desktop && !isBMPreview
            ? project.Banner?.downloadLink
            : undefined;

    const hero = (
        <DetailHero
            // A video banner keeps its clicks for the player.
            onBackgroundClick={backgroundVideo ? undefined : openViewer}
            title={project.ProjectHeroTitle || project.Title}
            backgroundImage={backgroundImage}
            backgroundVideo={backgroundVideo}
            overlay={project.Banner?.overlay}
            // The profile banner's parallax: the photograph lags the scroll at
            // a third of the page's rate and the title runs slightly ahead of
            // it, which is what reads as depth. Scaled past its box so the lag
            // never drags an edge into view.
            parallax
            // Below md the banner is cut to the height that puts the
            // title under it on the product page's pixel (384 at 375
            // wide, under the product's gallery), so the two pages open
            // on the same word in the same place.
            aspectClassName="max-md:aspect-[375/384] md:aspect-[1200/868] md:max-h-[86.8125rem]"
            heroStyle={heroStyle}
            heroUnderlaySrc={underlaySrc}
            // Fade the hero's bottom edge to the intro section's #181818 so it
            // blends seamlessly into the dark section below. Bottom-snapped and
            // capped at the Figma's 590px (36.875rem) so the fade stays in the
            // lower band rather than washing the whole hero (#181818 fading to
            // transparent by ~70% of that band).
            scrimClassName="absolute inset-x-0 bottom-0 h-[36.875rem] bg-[linear-gradient(to_top,#181818_6.21%,rgba(24,24,24,0)_70.17%)]"
            // A tenth of the page's own ground laid over the whole banner, so
            // the photograph reads as part of the dark page rather than a
            // bright window cut into it. Light enough to cost the image
            // nothing; enough to take the edge off a sunlit exterior.
            tintClassName="absolute inset-0 bg-[#181818]/10"
            // Subtle top fade (mobile) so the transparent top nav stays
            // legible over bright banners — light skies otherwise swallow
            // the white logo and icons.
            topScrimClassName="absolute inset-x-0 top-0 h-[7.5rem] bg-[linear-gradient(to_bottom,rgba(0,0,0,0.4),rgba(0,0,0,0))] md:hidden"
            // The product page's title, in its type and on its pixels:
            // 80px medium, 447px down and 40px in, so the two pages open
            // on the same word in the same place. Light rather than the
            // product's ink, since here it sits on the photograph.
            // Below md the title is not drawn on the banner: it sits under
            // it, as the product's does, so the h1 here is kept for
            // screen readers alone.
            contentClassName="max-md:sr-only md:top-[27.9375rem] md:right-[3rem] md:bottom-auto md:left-[2.5rem] md:max-w-none"
            titleClassName="font-medium text-[#e5e5e5] md:mt-0 md:text-[5rem] md:leading-[4.25rem] md:tracking-[-0.03em]"
            // The product page's row under the title, in white on the
            // photograph, carrying Back alone: the practice and the place
            // are stated under the banner. Stepped in by the half-rem the
            // title hangs out, so Back starts on the gutter as the
            // product's does. Desktop only: the mobile banner has no room
            // under its title.
            belowTitle={
                <DetailBylineRow
                    theme="dark"
                    className="max-md:hidden md:ml-[0.5rem]"
                    backFallbackLink={category?.Link ?? '/projects'}
                />
            }
        />
    );

    return (
        // The foot of the page before this one lands here: it puts this
        // banner where the banner it grew was.
        <div data-project-hero="">
            {canBoostBanner ? (
                <BoostResolutionOverlay
                    libraryImageId={bannerImageId}
                    currentImageUrl={backgroundImage}
                    displayWidth={upscaleTarget.width}
                    displayHeight={upscaleTarget.height}
                    wasUpscaled={!!project.Banner?.libraryImage?.WasUpscaled}
                    disabled={isBMPreview}
                    beforeClassName="top-30"
                    afterClassName="top-30"
                    // Boost joins Download in the photographs' own row.
                    controlInActionPanel
                >
                    <div className="group/hero relative">
                        {hero}
                        <HeroImageActions
                            downloadLink={readerDownloadLink}
                            libraryImageId={bannerImageId}
                        />
                    </div>
                </BoostResolutionOverlay>
            ) : (
                hero
            )}
            {/* A pixel of the page's own ground lapped over the banner's
                bottom edge. The scrim already fades the photograph to
                #181818 inside the hero, but the hero clips its box for the
                parallax, and at fractional browser zooms the scrim's painted
                edge can round a device pixel short of the box's — which
                showed as a hairline of raw photograph across the seam. The
                overlap swallows that rounding at any zoom. */}
            <div aria-hidden className="relative -mt-px h-px bg-[#181818]" />
            {/* Below md: the title and the practice under the banner, on the
                product page's pixels — its 28 under the picture, its title
                type, its byline a step down — light on this page's ground. */}
            <div className="md:hidden">
                <SectionSpacer height={64} mobileHeight={28} />
                <div className="px-6">
                    <p className={cn(MOBILE_DETAIL_TITLE, 'text-[#e5e5e5]')}>
                        {project.ProjectHeroTitle || project.Title}
                    </p>
                    {professional?.Title ? (
                        <p
                            className={cn(
                                MOBILE_DETAIL_BYLINE,
                                'text-white/60',
                            )}
                        >
                            <RoccoLink to={professional.Link}>
                                {professional.Title}
                            </RoccoLink>
                        </p>
                    ) : null}
                </div>
            </div>
            {/* The professional's badge straddling the banner's lower edge,
                then the category trail with their profile views and saves on
                its line — the product page's opening, on the same pixels.
                The badge is desktop only: mobile states the professional in
                the summary's trust row. */}
            <DetailBadgeRow
                logo={
                    professional?.CompanyLogo
                        ? {
                              src: professional.CompanyLogo,
                              alt: `${professional.Title} logo`,
                              background: professional.LogoBackgroundColor,
                              link: professional.Link,
                              ariaLabel: `${professional.Title} profile`,
                          }
                        : undefined
                }
            />
            <DetailBreadcrumbBar
                theme="dark"
                // Desktop only. On the phone the trail leads the credits row
                // further down (pure wayfinding, with the tags), and the
                // profile's figures live in the trust row beside the CTAs,
                // under the name they describe — here they interrupted
                // hero → summary → Book a Meeting.
                className="max-md:hidden"
                stats={detailStatsFromProfileKpi(
                    professional?.AggregatedPublicKPI,
                )}
                trail={[
                    { label: 'Home', to: '/' },
                    { label: 'Projects', to: PagesEnum.PROJECTS },
                    ...(category?.PrettyTitle && category.Link
                        ? [{ label: category.PrettyTitle, to: category.Link }]
                        : []),
                ]}
            />
            {viewerOpen ? (
                <DetailImageOverlay
                    slides={slides}
                    index={viewerIndex}
                    onIndexChange={setViewerIndex}
                    onClose={() => setViewerOpen(false)}
                    pinId={project.ID}
                    pinType="ProjectPin"
                    title={project.Title}
                    visitWebsite={
                        professional.Website
                            ? {
                                  website: professional.Website,
                                  libraryItemID: project.ID,
                                  professionalID: professional.ID,
                                  professionalTitle: professional.Title,
                              }
                            : null
                    }
                />
            ) : null}
        </div>
    );
};
